import React, { createContext, useState, useContext, useEffect } from 'react';
import { getCurrentUser, logout as apiLogout, API_BASE_URL } from '../services/api';

const AppContext = createContext();

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  const [eventData, setEventData] = useState(null);
  const [userData, setUserData] = useState(null);
  const [authData, setAuthData] = useState(null); // Authenticated user account
  const [authLoading, setAuthLoading] = useState(true); // Loading auth state
  const [toast, setToast] = useState(null);
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0); // Pending ride requests count for header badge
  const [newConfirmationsCount, setNewConfirmationsCount] = useState(0); // New confirmations count (when driver confirms user's request)
  const [siteSettings, setSiteSettings] = useState({ 
    logo_url: null, 
    primary_color: '#0B2A4A', 
    secondary_color: '#0EA5E9', 
    accent_color: '#0EA5E9', 
    theme_mode: 'light' // Always light - dark mode removed
  });

  // Load data from localStorage on mount - FAST: trust cached data immediately
  useEffect(() => {
    const savedEventData = localStorage.getItem('eventData');
    const savedUserData = localStorage.getItem('userData');
    const savedAuthAccount = localStorage.getItem('authAccount');
    const savedAuthToken = localStorage.getItem('authToken');
    
    if (savedEventData) {
      setEventData(JSON.parse(savedEventData));
    }
    if (savedUserData) {
      setUserData(JSON.parse(savedUserData));
    }
    
    // Trust cached auth data immediately for fast navigation
    if (savedAuthToken && savedAuthAccount) {
      try {
        setAuthData(JSON.parse(savedAuthAccount));
      } catch (e) {
        // Invalid cached data
      }
      // Verify in background (don't block navigation)
      const verifyInBackground = async () => {
        try {
          const response = await getCurrentUser();
          setAuthData(response.account);
          localStorage.setItem('authAccount', JSON.stringify(response.account));
        } catch (error) {
          // Token invalid or expired - clear auth state
          localStorage.removeItem('authToken');
          localStorage.removeItem('authAccount');
          setAuthData(null);
        }
      };
      verifyInBackground();
    }
    
    // Always set loading to false immediately
    setAuthLoading(false);
  }, []);

  // Load site settings from API
  const loadSiteSettings = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/site-settings`);
      if (response.ok) {
        const data = await response.json();
        const newSettings = data.settings || { 
          logo_url: null, 
          primary_color: '#0B2A4A', 
          secondary_color: '#0EA5E9', 
          accent_color: '#0EA5E9', 
          theme_mode: 'light' 
        };
        // Force theme_mode to always be 'light' - dark mode removed
        newSettings.theme_mode = 'light';
        console.log('Loaded site settings:', newSettings);
        setSiteSettings(newSettings);
        
        // Always apply light theme - dark mode removed
        document.documentElement.setAttribute('data-theme', 'light');
      } else {
        console.error('Failed to load site settings:', response.status, response.statusText);
        // Ensure light theme even if API fails
        document.documentElement.setAttribute('data-theme', 'light');
      }
    } catch (error) {
      console.error('Error loading site settings:', error);
      // Ensure light theme even if API fails
      document.documentElement.setAttribute('data-theme', 'light');
    }
  };

  // Load site settings on mount
  useEffect(() => {
    // Force light theme immediately on mount
    document.documentElement.setAttribute('data-theme', 'light');
    loadSiteSettings();
  }, []);

  // Save to localStorage when data changes
  useEffect(() => {
    if (eventData) {
      localStorage.setItem('eventData', JSON.stringify(eventData));
    }
  }, [eventData]);

  useEffect(() => {
    if (userData) {
      localStorage.setItem('userData', JSON.stringify(userData));
    }
  }, [userData]);
  
  useEffect(() => {
    if (authData) {
      localStorage.setItem('authAccount', JSON.stringify(authData));
    }
  }, [authData]);

  // Toast notification handler
  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  };

  const clearData = () => {
    setEventData(null);
    setUserData(null);
    localStorage.removeItem('eventData');
    localStorage.removeItem('userData');
  };
  
  // Logout function
  const logout = async () => {
    try {
      await apiLogout();
    } catch (error) {
      // Ignore errors, clear local state anyway
    }
    setAuthData(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('authAccount');
    showToast('Logged out successfully', 'success');
  };
  
  // Check if user is authenticated
  const isAuthenticated = !!authData;

  const value = {
    eventData,
    setEventData,
    userData,
    setUserData,
    authData,
    setAuthData,
    authLoading,
    isAuthenticated,
    logout,
    showToast,
    clearData,
    toast,
    siteSettings,
    loadSiteSettings,
    pendingRequestsCount,
    setPendingRequestsCount,
    newConfirmationsCount,
    setNewConfirmationsCount
  };

  return (
    <AppContext.Provider value={value}>
      {children}
      {toast && (
        <div className={`toast toast-${toast.type}`}>
          <div>{toast.message}</div>
        </div>
      )}
    </AppContext.Provider>
  );
};
